<template>
	<v-container fluid class="form-page-gutter">
		<v-form
			ref="form"
			v-model="formValid"
			class="d-flex justify-space-between flex-wrap flex-md-nowrap flex-lg-nowrap"
			style="gap: 5px">
			<v-card class="rounded-xl" elevation="0" outlined width="100%">
				<v-card-title class="expense-form-header">
					<div class="expense-form-title">
						<span v-if="!isUpdating">New Expense</span>
						<span v-else>Expense Detail</span>
					</div>
					<div class="expense-form-actions">
						<v-btn
							v-if="!isUpdating"
							color="primary"
							class="mr-1"
							rounded
							depressed
							icon
							@click="importDialog = true">
							<v-icon>mdi-upload</v-icon>
						</v-btn>
						<v-btn
							v-if="!isUpdating"
							color="primary"
							depressed
							rounded
							@click="addExpense"
							:loading="isLoading"
							:disabled="!formValid">
							<v-icon small class="mr-2">mdi-send</v-icon>
							Submit
						</v-btn>
						<v-btn
							v-else
							class="mr-1"
							color="primary"
							depressed
							rounded
							@click="updateExpense"
							:loading="isLoading"
							:disabled="!formValid">
							<v-icon small class="mr-2">mdi-send</v-icon>
							Update
						</v-btn>
						<v-btn
							v-if="isUpdating"
							color="error"
							icon
							:loading="isDeleteLoading"
							:disabled="!isRoot"
							@click="deleteExpense">
							<v-icon>mdi-delete</v-icon>
						</v-btn>
					</div>
				</v-card-title>
				<v-card-text>
					<v-container>
						<v-autocomplete
							class="mb-2"
							:items="types"
							item-text="displayName"
							item-value="id"
							:rules="dropdownRules"
							dense
							rounded
							outlined
							hide-details
							label="Type"
							v-model="expense.typeId">
							<template v-slot:no-data>
								<v-card elevation="0">
									<v-card-text class="text-center">
										<h4 class="mb-2">Type not found</h4>
										<v-btn
											depressed
											small
											rounded
											color="primary"
											@click="
												openDropdownOptionDialog(
													typeDropdownId
												)
											">
											<v-icon small class="mr-1">mdi-plus</v-icon>
											Add Type
										</v-btn>
									</v-card-text>
								</v-card>
							</template>
						</v-autocomplete>
						<v-text-field
							class="mb-2"
							:rules="nonNegativeRules"
							dense
							rounded
							outlined
							hide-details
							label="Amount"
							prefix="$"
							v-model.number="expense.amount"></v-text-field>
						<v-autocomplete
							class="mb-2"
							:rules="[(v) => !!v || '']"
							rounded
							outlined
							dense
							hide-details
							label="Vendor"
							item-value="id"
							item-text="name"
							:items="vendors"
							v-model="expense.vendorId">
							<template v-slot:no-data>
								<v-card elevation="0">
									<v-card-text class="text-center">
										<h4 class="mb-2">Vendor Not Found</h4>
										<v-btn
											depressed
											small
											rounded
											color="primary"
											@click="
												() => {
													vendorDialog =
														!vendorDialog;
												}
											">
											<v-icon small class="mr-2">mdi-plus</v-icon>
											New Vendor
										</v-btn>
									</v-card-text>
								</v-card>
							</template>
						</v-autocomplete>
						<v-autocomplete
							class="mb-2"
							:rules="dropdownRules"
							:items="paymentMethods"
							item-text="displayName"
							item-value="id"
							dense
							rounded
							outlined
							hide-details
							label="Payment Method"
							v-model="expense.paymentMethodId"
							required>
							<template v-slot:no-data>
								<v-card elevation="0">
									<v-card-text class="text-center">
										<h4 class="mb-2">Method not found</h4>
										<v-btn
											depressed
											small
											rounded
											color="primary"
											@click="
												openDropdownOptionDialog(
													paymentMethodDropdownId
												)
											">
											<v-icon small class="mr-1">mdi-plus</v-icon>
											Add Payment Method
										</v-btn>
									</v-card-text>
								</v-card>
							</template>
						</v-autocomplete>
						<div class="mb-2">
							<date-picker
								:rules="[(v) => !!v || 'Required']"
								v-model="expense.transactionDate"
								label="Transaction Date"
								:required="true"></date-picker>
						</div>

						<v-textarea
							:rules="textRules"
							dense
							rounded
							outlined
							label="Description"
							v-model="expense.description"
							required></v-textarea>
					</v-container>
				</v-card-text>
			</v-card>
			<v-card class="rounded-xl" elevation="0" outlined min-width="40%">
				<v-card-text>
					<v-container class="pa-4">
						<div
							class="d-flex align-center justify-space-between mb-4">
							<div class="expense-section-title">
								Expense Details
							</div>
							<v-chip
								v-if="expenseDetails.length > 0"
								color="primary"
								outlined
								small>
								{{ expenseDetails.length }}
								{{
									expenseDetails.length === 1
										? "Item"
										: "Items"
								}}
							</v-chip>
						</div>

						<v-divider class="mb-4"></v-divider>

						<div
							v-if="expenseDetails.length === 0"
							class="text-center py-8">
							<v-icon
								size="64"
								color="grey lighten-1"
								class="mb-3">
								mdi-file-document-outline
							</v-icon>
							<p class="grey--text text--darken-1 mb-1 text-h6">
								No expense details added yet
							</p>
							<p class="grey--text text-caption">
								Click "Add Detail" to get started
							</p>
						</div>

						<v-card
							v-for="(expenseDetail, idx) in expenseDetails"
							:key="`expenseDetail-${idx}`"
							class="mb-3 pa-3 rounded-xl"
							outlined
							elevation="0">
							<v-text-field
								:rules="textRules"
								dense
								outlined
								hide-details="auto"
								label="Name"
								v-model="expenseDetails[idx].name"
								class="rounded-xl mb-3"></v-text-field>

							<v-row dense>
								<v-col cols="12" sm="6">
									<v-text-field
										:rules="nonNegativeRules"
										dense
										outlined
										hide-details="auto"
										label="Quantity"
										type="number"
										:value="expenseDetails[idx].quantity"
										@input="
											expenseDetails[idx].quantity =
												$event ? parseFloat($event) : 0
										"
										class="rounded-xl"></v-text-field>
								</v-col>
								<v-col cols="12" sm="6">
									<v-text-field
										:rules="nonNegativeRules"
										dense
										outlined
										hide-details="auto"
										label="Unit Price"
										prefix="$"
										type="number"
										:value="expenseDetails[idx].unitPrice"
										@input="
											expenseDetails[idx].unitPrice =
												$event ? parseFloat($event) : 0
										"
										class="rounded-xl"></v-text-field>
								</v-col>
							</v-row>

							<v-divider class="my-2"></v-divider>

							<div class="d-flex justify-end">
								<v-btn
									x-small
									color="error"
									text
									@click="removeExpenseDetail(idx)">
									<v-icon x-small left>mdi-delete</v-icon>
									Delete
								</v-btn>
							</div>
						</v-card>

						<v-btn
							color="primary"
							depressed
							block
							rounded
							class="mt-2"
							@click="addExpenseDetail">
							<v-icon small left>mdi-plus</v-icon>
							Add Detail
						</v-btn>
					</v-container>
				</v-card-text>
			</v-card>
		</v-form>
		<import-expense-dialog v-model="importDialog"></import-expense-dialog>
		<vendor-dialog v-model="vendorDialog"></vendor-dialog>
	</v-container>
</template>
<script>
import DatePicker from "@/components/DatePicker.vue";
import ImportExpenseDialog from "@/components/ImportExpenseDialog.vue";
import VendorDialog from "@/components/VendorDialog.vue";
import generalMixin from "@/js/general-mixin";
import backendService from "@/services/backend-service";
import eventBus from "@/js/event-bus";
export default {
	name: "ExpenseForm",
	mixins: [generalMixin],
	components: {
		"date-picker": DatePicker,
		"import-expense-dialog": ImportExpenseDialog,
		"vendor-dialog": VendorDialog,
	},
	mounted: function () {
		if (this.$route.params.id) {
			this.getUpdateForm();
		} else if (this.isGenerating) {
			this.expense.vendorId = this.vendorId;
		}
	},
	props: {
		isGenerating: {
			type: Boolean,
			default: false,
		},
		vendorId: {
			type: Number,
			default: null,
		},
	},
	computed: {
		isRoot() {
			return this.$store.getters["isRoot"];
		},
		vendors() {
			return this.$store.getters["vendors"];
		},
		paymentMethods() {
			if (
				this.$store.getters["dropdownToOptions"] &&
				this.$store.getters["dropdownToOptions"]["expensePaymentMethod"]
			)
				return this.$store.getters["dropdownToOptions"][
					"expensePaymentMethod"
				].options;
			return [];
		},
		types() {
			if (
				this.$store.getters["dropdownToOptions"] &&
				this.$store.getters["dropdownToOptions"]["expenseType"]
			)
				return this.$store.getters["dropdownToOptions"]["expenseType"]
					.options;
			return [];
		},
		paymentMethodDropdownId() {
			if (
				this.$store.getters["dropdownToOptions"]["expensePaymentMethod"]
			)
				return this.$store.getters["dropdownToOptions"][
					"expensePaymentMethod"
				].id;
			return -1;
		},
		typeDropdownId() {
			if (this.$store.getters["dropdownToOptions"]["expenseType"])
				return this.$store.getters["dropdownToOptions"]["expenseType"]
					.id;
			return -1;
		},
		vendorDropdownId() {
			if (this.$store.getters["dropdownToOptions"]["vendor"])
				return this.$store.getters["dropdownToOptions"]["vendor"].id;
			return -1;
		},
	},
	data() {
		return {
			dropdownId: -1,
			dialog: false,
			expense: {},
			expenseDetails: [],
			importDialog: false,
			isLoading: false,
			isDeleteLoading: false,
			isUpdating: false,
			formValid: false,
			vendorDialog: false,
			dropdownRules: [
				(v) => {
					if (v) return true;
					return "Required";
				},
			],
			nonNegativeRules: [
				(v) => {
					if (v || v == 0) return true;
					return "Required";
				},
				(v) => {
					if (!isNaN(v)) return true;
					return "Must be numeric";
				},
			],
			textRules: [
				(v) => {
					if (v) return true;
					return "Required";
				},
			],
		};
	},
	methods: {
		addExpense() {
			if (!this.$refs.form.validate()) {
				return;
			}
			this.isLoading = true;
			this.expense.expenseDetails = this.expenseDetails;
			backendService
				.addExpense(this.expense)
				.then((response) => {
					this.timer(1000).then(() => {
						this.expense.id = response.data;
						this.isLoading = false;
						this.$router.replace({
							name: "ExpenseDetail",
							params: { id: this.expense.id },
						});
						this.getUpdateForm();
						this.successSnackbar("Successfully add new expense");
					});
				})
				.catch(() => {
					this.timer(1000).then(() => {
						this.isLoading = false;
						this.errorSnackbar("Ops! Something is not right");
					});
				});
		},
		addExpenseDetail() {
			this.expenseDetails.push({
				name: "",
				quantity: 0,
				unitPrice: 0,
			});
		},
		deleteExpense() {
			this.isDeleteLoading = true;
			backendService.deleteExpenseById(this.expense.id).then(() => {
				this.timer(1000)
					.then(() => {
						this.$store.commit("deleteExpense", this.expense);
						this.isDeleteLoading = false;
						this.successSnackbar("Successfully delete expense");
						this.$router.go(-1);
					})
					.catch(() => {
						this.timer(1000).then(() => {
							this.isDeleteLoading = false;
							this.errorSnackbar("Ops! Something is not right");
						});
					});
			});
		},
		getExpenseById(id) {
			backendService.getExpenseById(id).then((response) => {
				this.expense = response.data;
				this.expenseDetails = this.expense.expenseDetails;
			});
		},
		getUpdateForm() {
			this.isUpdating = true;
			this.getExpenseById(this.$route.params.id);
		},
		openDropdownOptionDialog(dropdownId) {
			eventBus.$emit("setDropdownOptionDialog", dropdownId);
		},
		updateExpense() {
			if (!this.$refs.form.validate()) return;
			this.isLoading = true;
			this.expense.expenseDetails = this.expenseDetails;
			backendService
				.updateExpense(this.expense)
				.then(() => {
					this.timer(1000).then(() => {
						this.isLoading = false;
						this.successSnackbar("Successfully updated expense");
					});
				})
				.catch(() => {
					this.timer(1000).then(() => {
						this.isLoading = false;
						this.errorSnackbar("Ops! Something is not right");
					});
				});
		},
		removeExpenseDetail(idx) {
			this.expenseDetails.splice(idx, 1);
		},
	},
	watch: {
		"$route.name": {
			handler: function (name) {
				if (name == "NewExpense") {
					this.isLoading = false;
					this.isUpdating = false;

					this.expenseDetails = [];
					this.expense = {};
				}
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>
<style scoped>
.expense-form-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
}
.expense-form-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
